import React, { useEffect, useMemo, useState } from "react";
import OrderPackagesHeader from "./OrderPackagesHeader";
import axios from "axios";
import OrderPackagesSkeleton from "./OrderPackageSkeleton";
import OrderPackagesTable from "./OrderPackagesTable";

const OrderPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [packageSortField, setPackageSortField] = useState(null);
  const [packageSortDirection, setPackageSortDirection] = useState("asc");
  const [packageSearchTerm, setPackageSearchTerm] = useState('');
  const [selectedPackages, setSelectedPackages] = useState(new Set());

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    axios
      .get("CourierPackage/GetUnshippedPackages")
      .then(({ data }) => {
        setPackages(data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  const handleSelectPackage = (packageId) => {
    setSelectedPackages((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(packageId)) {
        newSelected.delete(packageId);
      } else {
        newSelected.add(packageId);
      }
      return newSelected;
    });
  };

  // Efficient filtering and sorting function using useMemo
  const filteredAndSortedPackages = useMemo(() => {
    // First, filter the packages by status
    let result;
    if (selectedStatus === "All") {
      result = [...packages]; // Create a copy to avoid mutating the original array
    } else if (selectedStatus === "Ready") {
      result = packages.filter((pkg) => pkg.status.includes("Ready"));
    } else {
      result = packages.filter((pkg) => pkg.status === selectedStatus);
    }

    // Apply search term
    if (packageSearchTerm) {
        const searchLower = packageSearchTerm.toLowerCase();
        result = result.filter(pkg => 
          pkg.box?.name?.toLowerCase().includes(searchLower) ||
          pkg.order?.orderNumber?.toLowerCase().includes(searchLower) ||
          pkg.order?.shippingAddress?.recipientName?.toLowerCase().includes(searchLower)
        );
      }

    // Then, sort the filtered results if a sort field is specified
    if (packageSortField) {
      result.sort((a, b) => {
        const fieldA = a[packageSortField];
        const fieldB = b[packageSortField];

        if (typeof fieldA === "string" && typeof fieldB === "string") {
          return packageSortDirection === "asc"
            ? fieldA.localeCompare(fieldB)
            : fieldB.localeCompare(fieldA);
        } else {
          // For numeric fields or dates
          if (fieldA === null || fieldA === undefined)
            return packageSortDirection === "asc" ? -1 : 1;
          if (fieldB === null || fieldB === undefined)
            return packageSortDirection === "asc" ? 1 : -1;

          return packageSortDirection === "asc"
            ? fieldA - fieldB
            : fieldB - fieldA;
        }
      });
    }

    return result;
  }, [packages, selectedStatus, packageSortField, packageSortDirection,packageSearchTerm]);

  const handleSort = (field) => {
    if (packageSortField === field) {
      setPackageSortDirection(packageSortDirection === "asc" ? "desc" : "asc");
    } else {
      setPackageSortField(field);
      setPackageSortDirection("asc");
    }
  };

  const onStatusFilterChange = (value) => {
    setSelectedStatus(value)
    selectedPackages.clear();
  }

  const onSearchTermFilterChange = (value) => {
    console.log(value)
    setPackageSearchTerm(value || "")
  }

  const handlePrintLabels = () => {
    return;
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <OrderPackagesSkeleton />
        ) : (
          <>
            <OrderPackagesHeader
              packages={packages}
              onFilterChange={onStatusFilterChange}
              selectedPackages={selectedPackages}
              handlePrintLabels={handlePrintLabels}
              onSearchTermFilterChange={onSearchTermFilterChange}
            />
            <OrderPackagesTable
              id="default-material-table"
              packages={filteredAndSortedPackages}
              handleSort={handleSort}
              sortField={packageSortField}
              sortDirection={packageSortDirection}
              handleSelectPackage={handleSelectPackage}
              selectedPackages={selectedPackages}
              setSelectedPackages={setSelectedPackages}
              selectedStatus={selectedStatus}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OrderPackages;
